import { Card } from '@firi/ui-web';
import { t, Trans } from '@lingui/macro';
import { ArticleItem } from 'components/ArticlePost';
import { ArticleShowCase } from 'components/ArticleShowCase';
import { Button } from 'components/Button';
import ContentWrapper from 'components/ContentWrapper';
import { Grid, GridItem } from 'components/Grid';
import { PageMeta } from 'components/Page/PageMeta';
import PageBanner from 'components/PageBanner';
import SanityContent from 'components/SanityContent';
import { SEOCommon } from 'components/StructuredData/SEOCommon';
import { SEOFaq } from 'components/StructuredData/SEOFaq';
import { TableOfContents } from 'components/TableOfContents';
import { Typography } from 'components/Typography';
import {
  alternateSlugsQuery,
  contentQuery,
  getStartedPostsQuery,
  includeDrafts,
  signupPitchQuery,
} from 'constants/sanityQueries';
import { sanityParentSlugs } from 'constants/sanitySlugs';
import { findLocaleBySanityLanguage, findSanityLanguageByLocale } from 'helpers/language';
import { generateAlternateSlugFromSanity, getLocalizedUrl, getSanitySlug } from 'helpers/url';
import { useScrollToHeaders } from 'hooks/useScrollToHeaders';
import { getClient } from 'lib/sanity/sanity.server';
import { GetStaticPaths, GetStaticProps } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { SanityDownloadableContent } from 'types/sanity';
import { PageSlugV2, PageV2 } from 'types/schema';

export type AboutPageProps = Omit<PageV2, 'downloadableContent, signupPitchCards'> & {
  downloadableContent?: SanityDownloadableContent;
  signupPitchCards?: ArticleItem[];
};

const Page = ({ data }: { data: AboutPageProps }) => {
  const { headers, handleScrollTo } = useScrollToHeaders(data?.content);
  const router = useRouter();
  const isFAQ = data?.slugV2?.dynamic?.current.startsWith('faq');
  const hideTableOfContents = data?.hideTableOfContents ?? false;

  return (
    <div className="bg-white lg:bg-firi-light-grey">
      <PageMeta data={data} noIndex={data?.noIndex} />
      {isFAQ ? <SEOFaq data={data} /> : <SEOCommon />}
      <PageBanner {...data?.pageTopSection} />
      <ContentWrapper className="px-0 lg:px-4 py-4 md:py-12">
        <Grid>
          <GridItem className="hidden lg:block lg:col-span-3 self-start space-y-10">
            {!hideTableOfContents && (
              <nav aria-label="Table of contents">
                <Card className="lg:p-10">
                  <TableOfContents headers={headers} handleScrollTo={handleScrollTo} />
                </Card>
              </nav>
            )}
            {data?.downloadableContent && (
              <Card className="lg:p-10">
                <div className="mb-6">
                  <Typography size="lg" as="h2" color="blue">
                    <Trans>Downloadable content</Trans>
                  </Typography>
                </div>
                <ul className="space-y-6">
                  {data.downloadableContent.map((content) => (
                    <li key={content._key}>
                      <a href={`${content.url}?dl=`}>{content.title}</a>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </GridItem>
          <GridItem className="lg:col-span-9">
            <SanityContent blocks={data?.content} />
          </GridItem>
        </Grid>
        {data?.showGetStartedSection && data.signupPitchCards && (
          <section className="mt-4">
            <div className="flex justify-between items-center px-4 lg:px-0 mb-8">
              <div className="mr-4">
                <Typography as="h2" size="xl" color="blue">
                  <Trans>Get started</Trans>
                </Typography>
              </div>
              <Link
                href={getLocalizedUrl('/articles', router.locale, router.query)}
                locale={router.locale}
                passHref
              >
                <a>
                  <Button skin="outlined" aria-label={t`See all`} eventAction="Blog">
                    <Trans>See all</Trans>
                  </Button>
                </a>
              </Link>
            </div>

            <ArticleShowCase
              articles={data.signupPitchCards}
              horizontalScrollerClassName={'px-4 mr-0 lg:-mr-4'}
            />
          </section>
        )}
      </ContentWrapper>
    </div>
  );
};

export const getStaticProps: GetStaticProps = async ({ params, locale = '', preview = false }) => {
  const data = await getClient(preview).fetch(
    `*[_type == "pageV2" && (slugV2.predefined == $slug || slugV2.dynamic.current ==  $slug) && __i18n_lang == $locale ${includeDrafts(
      preview,
    )}] | order(_updatedAt desc) [0] {
      ...,
      downloadableContent[] {
        ...,
        "url": asset -> url
      },
      ${contentQuery},
      ${getStartedPostsQuery},
      ${signupPitchQuery},
      ${alternateSlugsQuery}
    }`,
    {
      slug: params?.slug,
      locale: findSanityLanguageByLocale(locale),
    },
  );

  if (!data) {
    return {
      notFound: true,
    };
  }

  const alternateSlugs = generateAlternateSlugFromSanity(data?.alternateSlugs || [], params);

  return {
    props: {
      preview,
      data,
      alternateSlugs,
    },
    revalidate: 600,
  };
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const pages = await getClient(false).fetch(
    `*[_type == "pageV2" && slugV2.parentSlug == $parentSlug ${includeDrafts(
      false,
    )}] { "slug": slugV2, "lang": __i18n_lang }`,
    {
      parentSlug: sanityParentSlugs.about,
    },
  );

  return {
    paths: pages
      ?.filter((page: { slug: PageSlugV2; lang: string }) =>
        locales?.includes(findLocaleBySanityLanguage(page.lang)),
      )
      ?.filter((page: { slug: PageSlugV2; lang: string }) => page.slug.dynamic?.current)
      .map((page: { slug: PageSlugV2; lang: string }) => ({
        params: { slug: getSanitySlug(page.slug) },
        locale: findLocaleBySanityLanguage(page.lang),
      })),
    fallback: true,
  };
};

export default Page;
